import { Client, ExternalApis } from '@app/core/services/api/API.service';
import { Injectable } from '@angular/core';
import { sortJSON } from './util.service';
import { DetailProduct } from '@app/shared/interfaces';

@Injectable({
    providedIn: 'root'
})
export class ModificatorPremiumProductService {
    calculateUnitDiscount(
        product: DetailProduct,
        client: Client,
        units: number
    ): number {
        let discountPercent = 0;

        if (!product || !product.clientDiscounts) {
            // console.error(
            //     'Product is undefined, null, or has no client discounts.'
            // );
            return 0;
        }

        product.clientDiscounts = sortJSON(
            product.clientDiscounts,
            'percent',
            'asc'
        );

        const applicableDiscounts = product.clientDiscounts.filter(
            (discount: any) => {
                return (
                    discount.clientId === client.id &&
                    discount.minUnits <= units &&
                    discount.maxUnits >= units
                );
            }
        );

        if (applicableDiscounts.length > 0) {
            discountPercent = applicableDiscounts[0].percent;
            // console.log(
            //     `El producto "${product.name}" tiene un descuento de ${discountPercent}% para [${applicableDiscounts[0].minUnits} - ${applicableDiscounts[0].maxUnits}] piezas para el cliente ${client.business_name}.`
            // );
        }

        return discountPercent;
    }

    calculateIncrement(
        product: DetailProduct,
        client: Client,
        apis: ExternalApis[] | null
    ): void {
        const incrementPercent =
            this.calculateIncrementPercent(product, client, apis) || 0;
        const base = product as any;

        if (base.__basePriceBeforeIncrement == null) {
            base.__basePriceBeforeIncrement = product.price;
        }
        if (base.__baseCardBaseAmmountBeforeIncrement == null) {
            base.__baseCardBaseAmmountBeforeIncrement = product.cardBaseAmmount;
        }

        if (!product.variants) {
            product.variants = [];
        }

        for (const variant of product.variants) {
            const v: any = variant as any;
            if (v.__basePriceBeforeIncrement == null) {
                v.__basePriceBeforeIncrement = variant.price;
            }
        }

        if (incrementPercent === 0) {
            product.incrementPercent = 0;
            product.increment = 0;
            product.incrementSaldo = 0;
            product.price = base.__basePriceBeforeIncrement;
            product.cardBaseAmmount = base.__baseCardBaseAmmountBeforeIncrement;
            for (const variant of product.variants) {
                const v: any = variant as any;
                variant.increment = 0;
                variant.price = v.__basePriceBeforeIncrement;
            }
            return;
        }

        const incrementFactor = 1 + incrementPercent / 100;
        product.incrementPercent = incrementPercent / 100;

        const nextProductPrice = parseFloat(
            (base.__basePriceBeforeIncrement * incrementFactor).toFixed(2)
        );
        product.increment = nextProductPrice;
        product.price = nextProductPrice;
        product.incrementSaldo = parseFloat(
            (nextProductPrice - base.__basePriceBeforeIncrement).toFixed(2)
        );

        for (const variant of product.variants) {
            const v: any = variant as any;
            const nextVariantPrice = parseFloat(
                (v.__basePriceBeforeIncrement * incrementFactor).toFixed(2)
            );
            variant.increment = nextVariantPrice;
            variant.price = nextVariantPrice;
        }

        if (base.__baseCardBaseAmmountBeforeIncrement > 0) {
            product.cardBaseAmmount = parseFloat(
                (
                    base.__baseCardBaseAmmountBeforeIncrement * incrementFactor
                ).toFixed(2)
            );
        }
    }

    calculateDiscounts(
        product: DetailProduct,
        client: Client,
        apis: ExternalApis[],
        units: number = 1
    ): void {
        const discountPercent = this.calculateDiscountPercent(
            product,
            client,
            apis,
            units
        );

        product.discountPercent = discountPercent ? discountPercent / 100 : 0;
        product.disc = discountPercent !== 0;
        product.discount =
            discountPercent !== 0
                ? parseFloat(
                      (
                          product.price -
                          product.price * product.discountPercent
                      ).toFixed(2)
                  )
                : null;

        if (product.variants) {
            for (const variant of product.variants) {
                variant.discountPercent = product.discountPercent || 0;
                variant.disc = discountPercent !== 0;
                variant.discount =
                    discountPercent !== 0
                        ? parseFloat(
                              (
                                  variant.price -
                                  variant.price * product.discountPercent
                              ).toFixed(2)
                          )
                        : null;
            }
        }

        if (product.isPurse && product.cardBaseAmmount) {
            if (discountPercent !== 0) {
                product.purse = product.purse || {};
                product.purse.discountPercent = product.discountPercent || 0;
                product.purse.disc = true;
                product.purse.discount = parseFloat(
                    (
                        product.cardBaseAmmount -
                        product.cardBaseAmmount * product.discountPercent
                    ).toFixed(2)
                );
            } else {
                product.purse = null;
            }
        }
    }

    getFeeIncrements(
        product: DetailProduct,
        client: Client,
        apis: ExternalApis[]
    ): void {
        let feePercent = 0;

        // Buscar un FEE exclusivo para el cliente en el producto
        /*if (product?.clientDiscounts?.length > 0) {
            product.clientDiscounts = this._utilService.sortJSON(
                product.clientDiscounts,
                'percent',
                'desc'
            );
            const clientDiscount = product.clientDiscounts?.find(
                (cd: any) => cd.clientId === client.id
            );
            if (clientDiscount && clientDiscount.percent) {
                discountPercent = clientDiscount.percent;
            }
        }*/
        // Buscar un FEE específico para el cliente en las APIs
        if (feePercent === 0 && product.nameExternal) {
            const api = apis.find(
                api => api.nameExternal === product.nameExternal
            );
            if (api?.clientFeeIncrements) {
                api.clientFeeIncrements = sortJSON(
                    api.clientFeeIncrements,
                    'percent',
                    'desc'
                );
                const clientFee = api.clientFeeIncrements?.find(
                    cd => cd.clientId === client.id
                );
                if (clientFee?.percent) {
                    feePercent = clientFee.percent;
                }
            }
        }
        // Buscar un FEE en la categoría del producto
        if (
            feePercent === 0 &&
            product.category?.clientFeeIncrements?.length > 0
        ) {
            product.category.clientFeeIncrements = sortJSON(
                product.category.clientFeeIncrements,
                'percent',
                'desc'
            );
            const clientFee = product?.category?.clientFeeIncrements?.find(
                ci => ci.clientId === client.id
            );
            if (clientFee) {
                feePercent = clientFee.percent;
            }
        }
        if (feePercent === 0) {
            feePercent = client.fee ?? 0;
        }

        if (feePercent !== 0) {
            product.feePercent = feePercent / 100;
        }
    }

    calculateIncrementPercent(
        product: DetailProduct,
        client: Client,
        apis: ExternalApis[]
    ): number {
        let incrementPercent = 0;

        if (!client) {
            return incrementPercent;
        }

        // Prioridad 1: Incremento configurado en el producto
        if (product?.clientIncrements?.length > 0) {
            const clientIncrement = product?.clientIncrements?.find(
                ci => ci.clientId === client.id
            );
            if (clientIncrement?.percent) {
                incrementPercent = clientIncrement.percent;
            }
        }

        // Prioridad 2: Incremento específico para el cliente en las APIs
        if (incrementPercent === 0 && apis) {
            for (const api of apis) {
                if (product.nameExternal === api.nameExternal) {
                    const clientIncrement = api.clientIncrements?.find(
                        ci => ci.clientId === client.id
                    );
                    if (clientIncrement?.percent) {
                        incrementPercent = clientIncrement.percent;
                        break;
                    }
                }
            }
        }

        // Prioridad 4: Incremento en la categoría del producto
        if (
            incrementPercent === 0 &&
            product?.category?.clientIncrements?.length > 0
        ) {
            const clientIncrement = product?.category?.clientIncrements?.find(
                ci => ci.clientId === client.id
            );
            if (clientIncrement) {
                incrementPercent = clientIncrement.percent;
            }
        }

        // Prioridad 5: Incremento a nivel de cliente
        if (incrementPercent === 0) {
            incrementPercent = client.increment ?? 0;
        }

        // Prioridad 6: Incremento global en las APIs
        if (apis && product.nameExternal) {
            const apiGI = apis.find(
                api => api.nameExternal === product.nameExternal
            );
            if (apiGI) {
                const globalIncrementAPI =
                    (apiGI.increment ?? 0) - (apiGI.discount ?? 0);
                incrementPercent += globalIncrementAPI;
            }
        }

        return incrementPercent;
    }

    calculateDiscountPercent(
        product: DetailProduct,
        client: Client,
        apis: ExternalApis[],
        units: number
    ): number {
        let discountPercent = 0;
        let applicableDiscounts: any[] = [];
        let discountSource = '';

        if (!client) {
            return discountPercent;
        }

        if (product?.clientDiscounts?.length > 0) {
            applicableDiscounts = product.clientDiscounts.filter(
                (discount: any) =>
                    discount.clientId === client.id &&
                    units >= discount.minUnits &&
                    units <= discount.maxUnits
            );
            if (applicableDiscounts.length > 0) {
                discountSource = 'Producto';
            }
        }

        if (applicableDiscounts.length === 0) {
            for (const api of apis) {
                if (
                    product?.nameExternal === api.nameExternal &&
                    api?.clientDiscounts?.length > 0
                ) {
                    applicableDiscounts = api.clientDiscounts.filter(
                        (discount: any) =>
                            discount.clientId === client.id &&
                            units >= discount.minUnits &&
                            units <= discount.maxUnits
                    );
                    if (applicableDiscounts.length > 0) {
                        discountSource = 'API del cliente';
                        break;
                    }
                }
            }
        }

        if (
            applicableDiscounts.length === 0 &&
            product?.category?.clientDiscounts?.length > 0
        ) {
            applicableDiscounts = product.category.clientDiscounts.filter(
                (discount: any) =>
                    discount.clientId === client.id &&
                    units >= discount.minUnits &&
                    units <= discount.maxUnits
            );
            if (applicableDiscounts.length > 0) {
                discountSource = 'Categoría';
            }
        }

        if (applicableDiscounts.length > 0) {
            discountPercent = applicableDiscounts[0].percent;
            // console.log(
            //     `El producto "${product.name}" tiene un descuento de ${discountPercent}% para [${applicableDiscounts[0].minUnits} - ${applicableDiscounts[0].maxUnits}] piezas para el cliente ${client.business_name}. ` +
            //         `Fuente del descuento: ${discountSource}.`
            // );
        }

        return discountPercent;
    }
}
